import { useState, useLayoutEffect, useRef, forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from "../../../utils/cn";
import MoreOrLessButton from "../MoreOrLessButton/MoreOrLessButton";
import { isKeyPressed } from "../../../utils/isKeyPressed";
import { SPACEBAR_KEY_CODE, RETURN_KEY_CODE } from "../../../utils/Browser/keyCodes";
import { compact, get, isNull, noop } from 'lodash';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const ExpandableContent = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    children,
    className,
    style
  } = _ref;
  return /*#__PURE__*/_jsx("div", {
    className: className,
    style: style,
    children: /*#__PURE__*/_jsx("div", {
      ref: ref,
      className: "table w-full",
      children: children
    })
  });
});
ExpandableContent.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string.isRequired,
  style: PropTypes.object.isRequired
};
const Toggle = _ref2 => {
  let {
    className,
    text,
    id,
    onClick = noop,
    readOnly = false,
    withIcon = false
  } = _ref2;
  const handleOnKeyDown = e => {
    if (isKeyPressed(e, "Enter", RETURN_KEY_CODE) || isKeyPressed(e, " ", SPACEBAR_KEY_CODE)) {
      onClick(e);
    }
  };
  return /*#__PURE__*/_jsx("div", {
    className: className,
    children: readOnly ? text : /*#__PURE__*/_jsxs(_Fragment, {
      children: [!withIcon && /*#__PURE__*/_jsx("a", {
        id: `${id}-toggle-link`,
        className: "cursor-pointer hover:text-deep-blue focus:text-deep-blue focus:outline-solid-offset focus:border-focus-blue rounded py-2 px-4",
        role: "button",
        onClick: onClick,
        onKeyDown: handleOnKeyDown,
        tabIndex: 0,
        children: text
      }), withIcon && /*#__PURE__*/_jsx(MoreOrLessButton, {
        onClick: onClick,
        omitDefaultStyles: true,
        moreText: text,
        lessText: text,
        className: "hover:text-deep-blue focus:text-deep-blue"
      })]
    })
  });
};
Toggle.propTypes = {
  className: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  readOnly: PropTypes.bool
};
const Header = _ref3 => {
  let {
    header
  } = _ref3;
  return /*#__PURE__*/_jsx("div", {
    className: "flex items-center flex-wrap my-4 text-dark-grey uppercase tracking-wide",
    children: /*#__PURE__*/_jsx("h5", {
      className: "font-heavy-font",
      children: header
    })
  });
};
Header.propTypes = {
  header: PropTypes.string.isRequired
};
const GradientSvg = _ref4 => {
  let {
    color,
    open,
    id
  } = _ref4;
  const gradientClass = cn('h-6 w-full relative transition-opacity duration-300 ease-in-out -top-5 gradient-svg', {
    'opacity-0': open,
    'opacity-100': !open
  });
  return /*#__PURE__*/_jsxs("svg", {
    className: gradientClass,
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg",
    children: [/*#__PURE__*/_jsx("defs", {
      children: /*#__PURE__*/_jsxs("linearGradient", {
        id: `${id}Gradient`,
        x1: "0",
        x2: "0",
        y1: "0",
        y2: "1",
        children: [/*#__PURE__*/_jsx("stop", {
          offset: "0%",
          stopColor: color,
          stopOpacity: "0"
        }), /*#__PURE__*/_jsx("stop", {
          offset: "100%",
          stopColor: color,
          stopOpacity: "100"
        })]
      })
    }), /*#__PURE__*/_jsx("rect", {
      className: "h-full w-full",
      fill: `url(#${id}Gradient)`
    })]
  });
};
GradientSvg.propTypes = {
  color: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired
};
const ExpandableContainer = _ref5 => {
  let {
    children = null,
    readOnlyToggleText = false,
    header = null,
    id,
    lessText = 'Read Less',
    moreText = 'Read More',
    withIcon = false,
    style = {},
    togglePosition = 'left',
    hideToggle = false,
    showGradient = true,
    collapsedHeight: propCollapsedHeight = '10em',
    useFirstChildHeight = false,
    isOpen: propIsOpen = false,
    forceResize = false,
    controlled = false,
    onToggle = noop
  } = _ref5;
  const [canExpand, setCanExpand] = useState(false);
  const [childHeight, setChildHeight] = useState(0);
  const [collapsedHeight, setCollapsedHeight] = useState(0);
  const [open, setOpen] = useState(propIsOpen);
  const elRef = useRef(null);
  const contentRef = useRef(null);
  const getChildHeight = () => {
    if (!contentRef.current) return;
    let newChildHeight = contentRef.current.clientHeight;
    if (useFirstChildHeight) {
      newChildHeight = get(contentRef.current, 'childNodes[0].clientHeight', newChildHeight);
    }
    setCanExpand(newChildHeight > collapsedHeight);
    setChildHeight(newChildHeight);
  };
  const getContentStyle = () => ({
    height: open || !canExpand ? `${childHeight}px` : propCollapsedHeight
  });
  const init = () => {
    const el = elRef.current;
    if (!el) return;
    const contentEl = document.createElement('div');
    contentEl.setAttribute('style', `height: ${propCollapsedHeight}; position: absolute;`);
    el.appendChild(contentEl);
    const elHeight = contentEl.clientHeight;
    el.removeChild(contentEl);
    setCollapsedHeight(elHeight);
    getChildHeight();
  };
  const onResize = () => {
    getChildHeight();
  };
  const toggle = e => {
    if (!withIcon) {
      e.preventDefault();
    }
    if (!controlled) {
      setOpen(!open);
    }
    if (onToggle) {
      onToggle(e);
    }
  };
  useLayoutEffect(() => {
    init();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', onResize);
      }
    };
  }, []);
  useLayoutEffect(() => {
    if (propIsOpen !== undefined && propIsOpen !== open) {
      setOpen(propIsOpen);
    }
  }, [propIsOpen]);
  useLayoutEffect(() => {
    if (forceResize) {
      onResize();
    }
  }, [forceResize]);
  useLayoutEffect(() => {
    getChildHeight();
  }, [children]);
  useEffect(() => {
    if (!contentRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        let newChildHeight = entry.contentRect.height;
        if (useFirstChildHeight) {
          newChildHeight = get(entry.target, 'childNodes[0].clientHeight', newChildHeight);
        }
        setCanExpand(newChildHeight > collapsedHeight);
        setChildHeight(newChildHeight);
      }
    });
    observer.observe(contentRef.current);
    return () => {
      if (observer && contentRef.current) {
        observer.unobserve(contentRef.current);
      }
    };
  }, [contentRef.current, collapsedHeight, useFirstChildHeight]);
  const toggleClass = cn("mb-2", {
    open,
    hidden: !canExpand
  }, {
    'flex justify-end': togglePosition === 'right' || togglePosition === 'top-right'
  });
  const expandableClass = cn('overflow-hidden transition-all duration-300 ease-in-out', {
    'h-inherit': open
  });
  const headerEl = header ? /*#__PURE__*/_jsx(Header, {
    header: header
  }, header) : null;
  const expandableContent = children ? /*#__PURE__*/_jsx(ExpandableContent, {
    ref: contentRef,
    className: expandableClass,
    style: getContentStyle(),
    children: children
  }, `${header}-content`) : null;
  const toggleEl = hideToggle ? null : /*#__PURE__*/_jsx(Toggle, {
    className: toggleClass,
    id: id,
    onClick: toggle,
    text: open ? lessText : moreText,
    withIcon: withIcon,
    readOnly: readOnlyToggleText
  }, `${header}-toggle`);
  const gradientColor = style.background || style.backgroundColor || 'white';
  const gradientEl = showGradient && canExpand ? /*#__PURE__*/_jsx(GradientSvg, {
    open: open,
    color: gradientColor,
    id: id
  }, `${header}-gradient`) : null;
  const items = compact([headerEl, ...(togglePosition.includes('top') ? [toggleEl, expandableContent, gradientEl] : [expandableContent, gradientEl, toggleEl])]);
  return /*#__PURE__*/_jsx("div", {
    className: "relative",
    id: id,
    ref: elRef,
    style: style,
    children: items.map(item => item)
  });
};
ExpandableContainer.propTypes = {
  /** Items that are passed to ExpandableContent */
  children: PropTypes.node,
  /** The height of the container while collapsed */
  collapsedHeight: PropTypes.string,
  /** Use the height of the first child while collapsed */
  useFirstChildHeight: PropTypes.bool,
  /** Boolean that determines if the component toggling is controlled externally */
  controlled: PropTypes.bool,
  /** Boolean that resizes the height depending on actions taken (radio, button, etc) */
  forceResize: PropTypes.bool,
  /** Header text for component */
  header: PropTypes.string,
  /** ID for main component used to build related IDs */
  id: PropTypes.string.isRequired,
  /** Prop used to determine if the container is open, used in conjunction with onToggle */
  isOpen: function (props, propName, componentName) {
    if (props.controlled === true && isNull(props[propName])) {
      return new Error(`When the prop 'controlled' is set to true, the '${propName}' prop for ${componentName} must be set to control toggling`);
    }
  },
  /** Boolean to hide the toggle button, used in conjunction with controlled */
  hideToggle: PropTypes.bool,
  /** Toggle text for expanded state */
  lessText: PropTypes.string,
  /** Toggle text for collapsed state */
  moreText: PropTypes.string,
  /** Boolean to show a toggle icon */
  withIcon: PropTypes.bool,
  /** Function that is called when the Toggle component is clicked */
  onToggle: PropTypes.func,
  /** Boolean to make the toggle text read-only or a functional toggle */
  readOnlyToggleText: PropTypes.bool,
  /** Styles object, override if feeling adventurous */
  style: PropTypes.object,
  /** String prop to control where the toggle is rendered in relation to the expanding content */
  togglePosition: PropTypes.oneOf(['left', 'right', 'top-right', 'top-left']),
  /** Boolean value to display or hide gradient */
  showGradient: PropTypes.bool
};
export default ExpandableContainer;
//# sourceMappingURL=ExpandableContainer.js.map